// Shown instead of the portal once a visitor's free month or 3-month trial is
// over, or when their licence cannot be confirmed at all.
//
// The sandbox is NOT deleted at this point — their data is still there, which
// is the whole argument for converting rather than starting again. Say that
// plainly and give them one way forward.

const C = { ink: "#16122b", muted: "#625d7a", violet: "#6248e0" };

const shell = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  overflowY: "auto",
  fontFamily: "Inter, sans-serif",
  background:
    "radial-gradient(42rem 26rem at 78% -12%, rgba(255,130,87,.24), transparent 62%)," +
    "radial-gradient(40rem 26rem at 8% 108%, rgba(123,96,255,.46), transparent 62%)," +
    "linear-gradient(155deg,#120d24 0%,#1d1440 44%,#2f2069 100%)",
};

const card = {
  width: "100%",
  maxWidth: 470,
  borderRadius: 26,
  padding: "38px 32px 30px",
  background: "#fff",
  boxShadow: "0 30px 70px -20px rgba(23,18,46,.6)",
  textAlign: "center",
};

const cta = {
  width: "100%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "15px 22px",
  borderRadius: 999,
  border: 0,
  cursor: "pointer",
  fontSize: 15.5,
  fontWeight: 700,
  color: "#fff",
  textDecoration: "none",
  background: "linear-gradient(90deg,#4a32bd,#6248e0 55%,#9b86ff)",
};

const CONTACT_URL = "https://info.portal.online.ge/#contact";

// A licence we could not read at all is a different message from one that ran
// its course — telling someone their access "ended" when the server was simply
// unreachable would be a lie, and one that costs a lead.
//
// A lapsed FREE month and a lapsed TRIAL also need different endings: the first
// has somewhere to go (register for three months), the second has already had
// it and needs a conversation instead.
const COPY = {
  free_expired: {
    title: "Your free month has ended",
    body:
      "Thank you for trying the Staff Portal. Everything you set up is still saved. " +
      "Register your hotel and we will extend you to three months with every module " +
      "open — the daily operations report, staff accommodation, the employee survey " +
      "and the administration tools.",
    upgrade: "Get 3 months, free",
  },
  expired: {
    title: "Your 3-month trial has ended",
    body:
      "Thank you for trying the Staff Portal. Everything you set up is still saved — " +
      "get in touch and we can turn this into your hotel's own portal, with your staff " +
      "and your content, without starting again.",
    action: "Talk to us about a licence",
  },
  invalid: {
    title: "This demo is no longer active",
    body:
      "The licence for this account has been withdrawn. If you think that is a mistake, " +
      "get in touch and we will sort it out.",
    action: "Contact us",
  },
  unreachable: {
    title: "We couldn't check your licence",
    body:
      "The licence server did not answer, so we cannot confirm your access right now. " +
      "This is almost always temporary — please try again in a moment.",
  },
};

export default function LicenseEndedScreen({ reason, plan, expiresAt, onRetry, onUpgrade }) {
  const key = reason === "expired" && plan === "free" ? "free_expired" : reason;
  const copy = COPY[key] || COPY.unreachable;
  const ended = expiresAt ? new Date(expiresAt) : null;

  return (
    <div style={shell}>
      <div style={card}>
        <img
          src="/logo/logo192.png"
          alt=""
          width="54"
          height="54"
          style={{ borderRadius: 15, display: "block", margin: "0 auto 18px" }}
        />
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: "-.02em",
          }}
        >
          {copy.title}
        </h1>
        {ended && reason === "expired" && (
          <p style={{ margin: "10px 0 0", fontSize: 13.5, color: C.violet, fontWeight: 600 }}>
            Ended {ended.toLocaleDateString()}
          </p>
        )}
        <p style={{ margin: "14px 0 26px", fontSize: 14.5, lineHeight: 1.7, color: C.muted }}>
          {copy.body}
        </p>

        {copy.upgrade && onUpgrade ? (
          <button type="button" onClick={onUpgrade} style={cta}>
            {copy.upgrade}
          </button>
        ) : copy.action ? (
          <a href={CONTACT_URL} target="_blank" rel="noreferrer" style={cta}>
            {copy.action}
          </a>
        ) : (
          <button type="button" onClick={onRetry} style={cta}>
            Try again
          </button>
        )}

        {(copy.upgrade || copy.action) && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              background: "none",
              border: 0,
              padding: 0,
              marginTop: 16,
              color: C.muted,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Check again
          </button>
        )}
      </div>
    </div>
  );
}
